package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.DashboardDetail
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.todayIn

sealed class CategorySpendingScreenState {
    data object Loading : CategorySpendingScreenState()
    data class Error(val message: String) : CategorySpendingScreenState()
    data class Loaded(
        val categoryName: String,
        val range: SpendingRange,
        val totalSpending: Double,
        val transactions: List<Transaction>,
        val accountNames: Map<String, String> = emptyMap(),
        val institutionNames: Map<String, String?> = emptyMap(),
        val transactionDashboardIds: Map<String, String?> = emptyMap()
    ) : CategorySpendingScreenState()
}

@OptIn(ExperimentalCoroutinesApi::class)
class CategorySpendingViewModel(
    private val categoryName: String,
    private val range: SpendingRange,
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val dashboardUseCase: DashboardUseCase
) : ViewModel() {

    private val _state = MutableStateFlow<CategorySpendingScreenState>(CategorySpendingScreenState.Loading)
    val state = _state.asStateFlow()

    init {
        load()
    }

    fun load() {
        _state.value = CategorySpendingScreenState.Loading
        viewModelScope.launch {
            transactionRepository.refresh()
        }
        viewModelScope.launch {
            val dashboardsDetailsFlow = dashboardUseCase.GetDashboards().flatMapLatest { dashboardsResponse ->
                when (dashboardsResponse) {
                    is Response.Error -> flowOf(Response.Error(dashboardsResponse.message))
                    Response.Loading -> flowOf(Response.Loading)
                    is Response.Success -> {
                        val dashboards = dashboardsResponse.data
                        if (dashboards.isEmpty()) {
                            flowOf(Response.Success(emptyList<DashboardDetail>()))
                        } else {
                            val detailFlows = dashboards.map { dashboard ->
                                dashboardUseCase.GetDashboardDetail(dashboard.dashboardId)
                            }
                            combine(detailFlows) { detailsResponses ->
                                val error = detailsResponses.filterIsInstance<Response.Error>().firstOrNull()
                                if (error != null) {
                                    Response.Error(error.message)
                                } else if (detailsResponses.any { it is Response.Loading }) {
                                    Response.Loading
                                } else {
                                    val details = detailsResponses.filterIsInstance<Response.Success<DashboardDetail>>().map { it.data }
                                    Response.Success(details)
                                }
                            }
                        }
                    }
                }
            }

            combine(
                dashboardsDetailsFlow,
                accountUseCase.GetAccounts(),
                accountUseCase.GetPlaidItems(),
                transactionRepository.transactions
            ) { detailsResponse, accountsResponse, plaidItemsResponse, transactionsResponse ->
                when {
                    detailsResponse is Response.Error ->
                        CategorySpendingScreenState.Error(detailsResponse.message)
                    accountsResponse is Response.Error ->
                        CategorySpendingScreenState.Error(accountsResponse.message)
                    plaidItemsResponse is Response.Error ->
                        CategorySpendingScreenState.Error(plaidItemsResponse.message)
                    transactionsResponse is Response.Error ->
                        CategorySpendingScreenState.Error(transactionsResponse.message)
                    detailsResponse is Response.Success && accountsResponse is Response.Success && plaidItemsResponse is Response.Success && transactionsResponse is Response.Success -> {
                        val dashboards = detailsResponse.data
                        val dashboardTransactions = dashboards
                            .flatMap { it.transactions }
                            .distinctBy { it.transactionId }

                        if (accountsResponse.data.isEmpty()) {
                            CategorySpendingScreenState.Loaded(categoryName, range, 0.0, emptyList())
                        } else {
                            val rangeDateString = try {
                                val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
                                when (range) {
                                    SpendingRange.SevenDays -> today.minus(7, DateTimeUnit.DAY).toString()
                                    SpendingRange.OneMonth -> today.minus(1, DateTimeUnit.MONTH).toString()
                                    SpendingRange.ThreeMonths -> today.minus(3, DateTimeUnit.MONTH).toString()
                                    SpendingRange.SixMonths -> today.minus(6, DateTimeUnit.MONTH).toString()
                                    SpendingRange.OneYear -> today.minus(1, DateTimeUnit.YEAR).toString()
                                    SpendingRange.YearToDate -> LocalDate(today.year, 1, 1).toString()
                                }
                            } catch (e: Exception) {
                                ""
                            }

                            val filteredTransactions = dashboardTransactions.filter { tx ->
                                val isCorrectCategory = tx.getRecentSpendingCategoryBreakdownKey() == categoryName
                                isCorrectCategory && tx.date >= rangeDateString && tx.amount > 0 && !tx.isExcludedFromSpending()
                            }.sortedByDescending { it.date }

                            val total = filteredTransactions.sumOf { it.amount }

                            val accountNames = mutableMapOf<String, String>()
                            val institutionNames = mutableMapOf<String, String?>()

                            val plaidItems = plaidItemsResponse.data

                            // Populate from user's own profile accounts
                            accountsResponse.data.forEach { acc ->
                                accountNames[acc.accountId] = acc.name
                                val institutionName = plaidItems.find { it.itemId == acc.itemId }?.institutionName
                                institutionNames[acc.accountId] = institutionName
                            }

                            val transactionDashboardIds = mutableMapOf<String, String?>()
                            dashboards.forEach { dashboardDetail ->
                                dashboardDetail.transactions.forEach { tx ->
                                    transactionDashboardIds[tx.transactionId] = dashboardDetail.dashboardId
                                }
                            }

                            // Populate/overwrite from dashboard accounts (including shared)
                            dashboards.flatMap { it.accounts }.forEach { acc ->
                                accountNames[acc.accountId] = acc.name
                                institutionNames[acc.accountId] = acc.institutionName
                            }

                            CategorySpendingScreenState.Loaded(
                                categoryName = categoryName,
                                range = range,
                                totalSpending = total,
                                transactions = filteredTransactions,
                                accountNames = accountNames,
                                institutionNames = institutionNames,
                                transactionDashboardIds = transactionDashboardIds
                            )
                        }
                    }
                    else -> CategorySpendingScreenState.Loading
                }
            }.collect { _state.value = it }
        }
    }
}
